import { PdfGen, PdfRect, PdfUnit, PdfBrush, ETextAlign, PdfSize } from '../../pdf/pdf';
import { ILayoutListGen } from '../layoutList';
import { OptsList, getListItems } from '../optsList';
import { GUtil } from '../../pdf/gutil';
import { GeoBoard } from '../../pdf/board/geoboard';
import { C } from '../../core/c';
import { IItemCount } from '@educraft/base';
import '../../pdf/draw/primitives';
import '../../pdf/draw/string';
import '../../pdf/draw/shapes';

function generatePath(boardWidth: number, boardHeight: number, length: number, rnd: any): C[] {
  const maxRadiusSq = 25; // max radius = 5
  const maxTries = 4000;
  const minSegmentDistSq = 0.15;
  let tries = 0;

  function distToSegmentSq(
    px: number,
    py: number,
    vx: number,
    vy: number,
    wx: number,
    wy: number
  ): number {
    const l2 = (wx - vx) ** 2 + (wy - vy) ** 2;
    if (l2 === 0) return (px - vx) ** 2 + (py - vy) ** 2;
    let t = ((px - vx) * (wx - vx) + (py - vy) * (wy - vy)) / l2;
    t = Math.max(0, Math.min(1, t));
    const projX = vx + t * (wx - vx);
    const projY = vy + t * (wy - vy);
    return (px - projX) ** 2 + (py - projY) ** 2;
  }

  function dfs(currentPath: C[], visited: Set<string>): C[] | null {
    if (currentPath.length === length) return currentPath;
    if (tries++ > maxTries) return null;

    const current = currentPath[currentPath.length - 1];
    const candidates: { pt: C; crossed: string[] }[] = [];

    for (let x = 0; x < boardWidth; x++) {
      for (let y = 0; y < boardHeight; y++) {
        const pt = new C(x, y);
        const hash = `${pt.x},${pt.y}`;
        if (visited.has(hash)) continue;

        const dx = pt.x - current.x;
        const dy = pt.y - current.y;
        const distSq = dx * dx + dy * dy;

        // Limit distance to max radius 5
        if (distSq > 0 && distSq <= maxRadiusSq) {
          if (currentPath.length >= 2) {
            const prev = currentPath[currentPath.length - 2];
            const pdx = current.x - prev.x;
            const pdy = current.y - prev.y;
            const dot = dx * pdx + dy * pdy;

            // Prevent forth and back (sharp U-turns) by enforcing angle <= 90 degrees
            if (dot < 0) continue;
          }

          // Check for line crossing existing points or creating new "crossed" points
          const crossed: string[] = [];
          let isBlocked = false;
          for (let gx = 0; gx < boardWidth; gx++) {
            for (let gy = 0; gy < boardHeight; gy++) {
              if ((gx === current.x && gy === current.y) || (gx === pt.x && gy === pt.y)) continue;
              if (distToSegmentSq(gx, gy, current.x, current.y, pt.x, pt.y) < minSegmentDistSq) {
                const gHash = `${gx},${gy}`;
                if (visited.has(gHash)) {
                  isBlocked = true;
                  break;
                }
                crossed.push(gHash);
              }
            }
            if (isBlocked) break;
          }

          if (!isBlocked) {
            candidates.push({ pt, crossed });
          }
        }
      }
    }

    // Shuffle candidates to pick random smooth path
    for (let i = candidates.length - 1; i > 0; i--) {
      const j = rnd.next(0, i + 1);
      [candidates[i], candidates[j]] = [candidates[j], candidates[i]];
    }

    for (const cand of candidates) {
      const hash = `${cand.pt.x},${cand.pt.y}`;
      visited.add(hash);
      for (const cHash of cand.crossed) visited.add(cHash);
      currentPath.push(cand.pt);

      const res = dfs(currentPath, visited);
      if (res) return res;

      currentPath.pop();
      for (const cHash of cand.crossed) visited.delete(cHash);
      visited.delete(hash);
    }

    return null;
  }

  // Attempt to find path starting from random points
  for (let attempt = 0; attempt < 50; attempt++) {
    const startPt = new C(rnd.next(0, boardWidth), rnd.next(0, boardHeight));
    const visited = new Set<string>([`${startPt.x},${startPt.y}`]);
    tries = 0;
    const path = dfs([startPt], visited);
    if (path) return path;
  }

  // Fallback to fully random if search fails (e.g. very dense boards)
  const fallback: C[] = [];
  for (let y = 0; y < boardHeight; y++) {
    for (let x = 0; x < boardWidth; x++) {
      fallback.push(new C(x, y));
    }
  }
  for (let p = fallback.length - 1; p > 0; p--) {
    const j = rnd.next(0, p + 1);
    [fallback[p], fallback[j]] = [fallback[j], fallback[p]];
  }
  return fallback.slice(0, length);
}

/**
 * Shared logic for GeoBoard sequence generators (List version).
 */
function generateGeoBoard(pdf: PdfGen, opts: OptsList, board: GeoBoard): void {
  const items = getListItems(opts);
  if (items.length === 0) return;

  const maxPoints = board.width * board.height;
  const itemsPerPage = Math.min(items.length, maxPoints, 20); // 20 is a reasonable limit per board for legibility

  const rnd = opts.data.random;

  for (let i = 0; i < items.length; i += itemsPerPage) {
    const page = pdf.addPage();
    board.draw(page);
    board.drawMini(page);

    const currentItems = items.slice(i, i + itemsPerPage);

    // Generate a non-backtracking path with limited jump distance
    const selected = generatePath(board.width, board.height, currentItems.length, rnd);

    const margin1 = PdfUnit.FromMillimeters(1);
    const dist = board.distance;
    const font = page.adjustFont(
      dist - margin1,
      currentItems.map((it) => it.value),
      GUtil.baseFont
    );

    // Draw labeled boxes above selected peg-holes
    for (let index = 0; index < selected.length; index++) {
      const pt = selected[index];
      const pp = board.point(pt.x, pt.y);

      const r = new PdfRect(pp.x - dist / 2, pp.y - dist * 0.7, dist, dist * 0.5);
      const r1 = new PdfRect(r.x + margin1, r.y, r.width - 2 * margin1, dist * 0.9);

      page.drawRectangle(GUtil.basePen, null, r1);
      page.drawString(currentItems[index].value, font, PdfBrush.Black, null, r, ETextAlign.Center);
    }

    // Connect selected points on the mini board
    const miniPoints = selected.map((pt) => board.pointMini(pt.x, pt.y));
    if (miniPoints.length > 1) {
      page.drawLines(GUtil.basePen, miniPoints);

      const iconSize = board.distanceMini * 0.8;
      const startRect = PdfRect.circle(miniPoints[0], iconSize);
      const stopRect = PdfRect.circle(miniPoints[miniPoints.length - 1], iconSize);

      page.drawStart(startRect);
      page.drawStop(stopRect);
    }
  }
}

/**
 * GeoBoard__8__Posloupnost — sequence exercise on an 8×8 GeoBoard.
 * Port of C# EduFrame.Order.Generators.GeoBoard__8__Posloupnost
 */
export class GeoBoard__8__Posloupnost implements ILayoutListGen, IItemCount {
  countDefault(): number {
    return 10;
  }
  countMin(): number {
    return 2;
  }
  countMax(): number {
    return 50;
  }

  public async generate(pdf: PdfGen, opts: OptsList): Promise<void> {
    generateGeoBoard(pdf, opts, GeoBoard.Board8());
  }
}

/**
 * GeoBoard__920__Posloupnost — sequence exercise on a 9×9 GeoBoard (920 variant).
 * Port of C# EduFrame.Order.Generators.GeoBoard__920__Posloupnost
 */
export class GeoBoard__920__Posloupnost implements ILayoutListGen, IItemCount {
  countDefault(): number {
    return 10;
  }
  countMin(): number {
    return 2;
  }
  countMax(): number {
    return 50;
  }

  public async generate(pdf: PdfGen, opts: OptsList): Promise<void> {
    generateGeoBoard(pdf, opts, GeoBoard.Board920());
  }
}
